/**
 * An event's location, as structured parts over the single stored string.
 *
 * The admin form asks for a city and a state so those stop being free text that
 * every organizer punctuates differently. The database still holds one `location`
 * string, because that string is what the public calendar, the event page, and the
 * calendar invite already render — splitting the column would mean touching all of
 * them, and this needs no migration.
 *
 * So: parse the parts out for editing, format them back for storage. The format is
 * the one the existing catalog already uses, `[Venue, ]City, ST`, which is why
 * "The Board Studios, Dallas, TX" round-trips unchanged.
 *
 * Not every location is a place. "Zoom (link sent after registration)" has no city
 * and never will, and six of the catalog's eight events look like that. Those parse
 * as unstructured and are edited as free text rather than being forced through a
 * city field that would throw the detail away.
 */

export const US_STATES: Array<{ code: string; name: string }> = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" }, { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" }, { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" }, { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" }, { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" }, { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" }, { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" }, { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" }, { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" }, { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" }, { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

const STATE_CODES = new Set(US_STATES.map(state => state.code));

export type EventLocationParts = {
  /** Anything before the city — a venue, a building. Empty when there is none. */
  venue: string;
  city: string;
  /** A two-letter US state code, or "" when the location is not a US place. */
  state: string;
  /**
   * True when the string could not be read as `[Venue, ]City, ST`.
   *
   * The form uses this to keep offering a single free-text field, so a virtual
   * event's "Zoom (link sent after registration)" is editable as what it is instead
   * of being mangled into a city.
   */
  unstructured: boolean;
  /** The original string, so an unstructured location can be edited and saved as-is. */
  raw: string;
};

/**
 * Reads a stored location string into parts.
 *
 * Parsed from the end, because the state is the only reliably-shaped token: two
 * letters that name a state. Everything before the final comma is city, and
 * everything before that is venue. Working forwards would have to guess where a
 * venue name stops, and "The Board Studios, Dallas, TX" has no marker for that.
 */
export function parseEventLocation(value: string | null | undefined): EventLocationParts {
  const raw = (value ?? "").trim();
  const unparsed: EventLocationParts = { venue: "", city: "", state: "", unstructured: true, raw };
  if (!raw) return { ...unparsed, unstructured: false };

  const segments = raw.split(",").map(segment => segment.trim()).filter(Boolean);
  if (segments.length < 2) return unparsed;

  const last = segments[segments.length - 1] ?? "";
  // Tolerates a trailing note — "Dallas, TX (Venue TBD)" — by reading the state off
  // the front of the final segment and keeping the remainder with the city.
  const stateMatch = /^([A-Za-z]{2})\b(.*)$/.exec(last);
  const code = stateMatch?.[1]?.toUpperCase();
  if (!code || !STATE_CODES.has(code)) return unparsed;

  const trailingNote = (stateMatch?.[2] ?? "").trim();
  const city = [segments[segments.length - 2], trailingNote].filter(Boolean).join(" ").trim();
  if (!city) return unparsed;

  return {
    venue: segments.slice(0, -2).join(", "),
    city,
    state: code,
    unstructured: false,
    raw,
  };
}

/**
 * Formats parts back into the stored string.
 *
 * An unstructured location returns its raw value untouched, which is what makes the
 * round trip safe for the virtual events: editing an unrelated field on one of them
 * must not rewrite its location.
 */
export function formatEventLocation(parts: EventLocationParts): string {
  if (parts.unstructured) return parts.raw.trim();

  const city = parts.city.trim();
  const state = parts.state.trim().toUpperCase();
  const venue = parts.venue.trim();

  // A city with no state, or the reverse, is a half-finished edit rather than a
  // location. Returning what there is beats inventing punctuation around a blank.
  const place = [city, STATE_CODES.has(state) ? state : ""].filter(Boolean).join(", ");
  return [venue, place].filter(Boolean).join(", ");
}

/**
 * What view mode shows: "Dallas, TX".
 *
 * The venue is deliberately not included — the request is for city and state, and
 * the venue is already carried on its own line where there is room for it. An
 * unstructured location falls back to its raw string, because "Zoom (link sent after
 * registration)" is the whole answer for that event and hiding it would leave the
 * field blank.
 */
export function formatEventLocationDisplay(value: string | null | undefined): string {
  const parts = parseEventLocation(value);
  if (parts.unstructured) return parts.raw;
  const place = [parts.city, parts.state].filter(Boolean).join(", ");
  return place || parts.raw;
}
